import type { LocationQuery } from 'vue-router';

import { i18n } from '@/i18n';

export interface ActiveRouteMatch {
  excludedQuery?: Record<string, string>;
  query?: Record<string, string>;
  routeNames: string[];
}

export interface ActiveRouteConfig {
  activeRouteMatches?: ActiveRouteMatch[];
  activeRouteNames?: string[];
}

export interface Separator {
  id: number;
  type: 'separator';
}

export interface NavRouteItem extends ActiveRouteConfig {
  text: string;
  legacy?: string;
  to?: string;
}

export interface Route extends ActiveRouteConfig {
  id: string;
  text: string;
  icon?: string;
  routeName?: string;
  legacy?: string;
  to?: string;
  parentRouteName?: string;
  items?: NavRouteItem[];
  target?: string;
}

const matchesQuery = (query: LocationQuery, expectedQuery: Record<string, string>): boolean => {
  return Object.entries(expectedQuery).every(([key, value]) => query[key] === value);
};

export const isSidebarRouteActive = (
  menuRoute: ActiveRouteConfig,
  currentRouteNames: string[],
  query: LocationQuery,
): boolean => {
  const routeMatches = menuRoute.activeRouteMatches ?? [{ routeNames: menuRoute.activeRouteNames ?? [] }];

  return routeMatches.some((routeMatch) => {
    const matchesRouteName = routeMatch.routeNames.some(routeName => currentRouteNames.includes(routeName));
    const matchesRequiredQuery = !routeMatch.query || matchesQuery(query, routeMatch.query);
    const matchesExcludedQuery = routeMatch.excludedQuery && matchesQuery(query, routeMatch.excludedQuery);

    return matchesRouteName && matchesRequiredQuery && !matchesExcludedQuery;
  });
};

export const publicRoutes = [
  {
    id: 'features',
    to: '/blog',
    text: i18n.global.t('sidebar.features'),
  },
  {
    id: 'resources',
    legacy: 'https://help.erank.com/',
    text: i18n.global.t('sidebar.resources'),
  },
  {
    id: 'plans',
    text: i18n.global.t('sidebar.plans'),
    to: 'plans',
  },
] as (Route | Separator)[];

export const registeredRoutes = [
  {
    activeRouteNames: ['dashboard'],
    icon: 'home-alt',
    id: 'dashboard',
    text: i18n.global.t('sidebar.dashboard'),
    to: '/dashboard',
  },
  {
    activeRouteNames: ['keyword-research', 'bulk-keyword-tool', 'keyword-lists', 'rank-checker'],
    icon: 'search',
    id: 'keyword-research',
    text: i18n.global.t('sidebar.keyword_research.title'),
    items: [
      {
        activeRouteNames: ['keyword-research'],
        text: i18n.global.t('sidebar.keyword_research.keyword_tool'),
        to: '/keyword-tool',
      },
      {
        activeRouteNames: ['bulk-keyword-tool'],
        to: '/bulk-keyword-tool',
        text: i18n.global.t('sidebar.keyword_research.bulk_keyword_tool'),
      },
      {
        activeRouteNames: ['keyword-lists'],
        to: '/keyword-lists',
        text: i18n.global.t('sidebar.keyword_research.keyword_lists'),
      },
      {
        activeRouteNames: ['rank-checker'],
        to: '/rank-checker',
        text: i18n.global.t('sidebar.keyword_research.rank_checker'),
      },
    ],
  },
  {
    activeRouteNames: ['competitor-sales', 'top-sellers', 'shop-info', 'categories'],
    icon: 'bar-chart-alt-2',
    id: 'competitor',
    text: i18n.global.t('sidebar.competitor_research.title'),
    items: [
      {
        activeRouteNames: ['competitor-sales'],
        to: '/competitor-sales',
        text: i18n.global.t('sidebar.competitor_research.competitor_sales'),
      },
      {
        activeRouteNames: ['top-sellers'],
        to: '/top-sellers',
        text: i18n.global.t('sidebar.competitor_research.top_sellers'),
      },
      {
        activeRouteNames: ['shop-info'],
        to: '/shop-info',
        text: i18n.global.t('sidebar.competitor_research.shop_info'),
      },
      {
        activeRouteNames: ['categories'],
        to: '/category',
        text: i18n.global.t('sidebar.competitor_research.category'),
      },
    ],
  },
  {
    activeRouteNames: [
      'listings',
      'listing-changes',
      'listing-audit-index',
      'listing-audit',
      'listing-not-found',
      'health-check',
      'ai-listing-helper',
      'compare-listings',
      'create-video',
    ],
    icon: 'purchase-tag-alt',
    id: 'listing-optimization',
    text: i18n.global.t('sidebar.listing_optimization.title'),
    items: [
      {
        activeRouteMatches: [
          {
            excludedQuery: { ref: 'side-menu' },
            routeNames: ['listings'],
          },
        ],
        to: '/listings/active',
        text: i18n.global.t('sidebar.listing_optimization.listing_audits'),
      },
      {
        activeRouteNames: ['listing-audit-index', 'listing-audit', 'listing-not-found'],
        to: '/listing-audit',
        text: i18n.global.t('sidebar.listing_optimization.listing_audit_landing'),
      },
      {
        activeRouteNames: ['health-check'],
        text: i18n.global.t('sidebar.listing_optimization.health_check'),
        to: '/health-check',
      },
      {
        activeRouteNames: ['ai-listing-helper'],
        text: i18n.global.t('sidebar.listing_optimization.al_listing_helper'),
        to: '/ai-listing-helper',
      },
      {
        activeRouteNames: ['compare-listings'],
        text: i18n.global.t('sidebar.listing_optimization.compare_listings'),
        to: '/compare-listings',
      },
      {
        activeRouteMatches: [
          {
            query: { ref: 'side-menu' },
            routeNames: ['listings'],
          },
          {
            routeNames: ['listing-changes'],
          },
        ],
        to: '/listings/active?ref=side-menu',
        text: i18n.global.t('sidebar.listing_optimization.changes'),
      },
      {
        activeRouteNames: ['create-video'],
        text: i18n.global.t('sidebar.listing_optimization.create_video'),
        to: '/create-video',
      },
    ],
  },
  {
    activeRouteNames: ['traffic-stats-parent', 'spotted-on-etsy', 'tags', 'tag-occurrences', 'sales-map'],
    icon: 'store-alt',
    id: 'shop',
    text: i18n.global.t('sidebar.shop_insights.title'),
    items: [
      {
        activeRouteNames: ['traffic-stats-parent'],
        text: i18n.global.t('sidebar.shop_insights.traffic_stats'),
        to: '/traffic-stats',
      },
      {
        activeRouteNames: ['spotted-on-etsy'],
        text: i18n.global.t('sidebar.shop_insights.spotted_on_etsy'),
        to: '/spotted-on-etsy',
      },
      {
        activeRouteNames: ['tags', 'tag-occurrences'],
        text: i18n.global.t('sidebar.shop_insights.tag_report'),
        to: '/tags',
      },
      {
        activeRouteNames: ['sales-map'],
        to: '/sales-map',
        text: i18n.global.t('sidebar.shop_insights.sales_map'),
      },
    ],
  },
  {
    activeRouteNames: ['trend-buzz', 'trending'],
    icon: 'fire',
    id: 'trends',
    text: i18n.global.t('sidebar.trends.title'),
    items: [
      {
        activeRouteNames: ['trend-buzz'],
        text: i18n.global.t('sidebar.trends.trend_buzz'),
        to: '/trend-buzz',
      },
      {
        activeRouteNames: ['trending'],
        text: i18n.global.t('sidebar.trends.monthly_trends'),
        to: '/trending',
      },
    ],
  },
  {
    activeRouteNames: ['calculators', 'shortcut'],
    icon: 'wrench',
    id: 'tools',
    text: i18n.global.t('sidebar.tools.title'),
    items: [
      {
        activeRouteNames: ['calculators'],
        text: i18n.global.t('sidebar.tools.calculators'),
        to: '/calculators',
      },
      {
        activeRouteNames: ['shortcut'],
        to: '/shortcut',
        text: i18n.global.t('sidebar.tools.shortcut_button'),
      },
    ],
  },
  {
    id: 1,
    type: 'separator',
  },
  {
    activeRouteNames: ['extension'],
    icon: 'extension-alt',
    id: 'extension',
    to: '/extension',
    text: i18n.global.t('sidebar.extension'),
  },
  {
    activeRouteNames: ['settings', 'settings.confirm-email'],
    icon: 'cog-alt',
    id: 'settings',
    to: '/settings',
    text: i18n.global.t('sidebar.settings'),
  },
] as (Route | Separator)[];
